import React, { useEffect, useState, useCallback } from "react";
import { ClipboardList, Search, Truck, Package as PackageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, fmtDate, relDay } from "@/lib/format";
import { STATUS_BADGE, STATUS_LABEL, PAYMENT_BADGE, PAYMENT_LABEL, PRIORITY_BADGE, PRIORITY_LABEL } from "@/lib/constants";
import { Card, Loading, EmptyState, PageHeader, Badge } from "@/components/common";
import OrderDetailModal from "@/components/OrderDetailModal";

const FILTERS = [
  ["", "Semua"],
  ["new", "Order Baru"],
  ["to_arrange", "Antre Rangkai"],
  ["arranging", "Dirangkai"],
  ["ready_for_qc", "Siap QC"],
  ["qc_failed", "QC Gagal"],
  ["ready_pickup", "Siap Diambil"],
  ["ready_delivery", "Siap Kirim"],
  ["out_for_delivery", "Dikirim"],
  ["unpaid", "Belum Lunas"],
  ["overdue", "Terlambat"],
  ["completed", "Selesai"],
];

const RANGES = [["all", "Semua"], ["today", "Hari Ini"], ["tomorrow", "Besok"], ["week", "Minggu Ini"], ["month", "Bulan Ini"]];

export default function OrderCenter() {
  const { branch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [range, setRange] = useState("all");
  const [q, setQ] = useState("");
  const [fulfillment, setFulfillment] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { range, sort: "due", limit: 300 };
      if (branch) params.branch_id = branch.id;
      if (filter) params.filter = filter;
      if (q) params.q = q;
      if (fulfillment) params.fulfillment = fulfillment;
      const d = await api.get("/orders/center", params);
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [branch, filter, range, q, fulfillment]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const summary = data?.summary || {};

  return (
    <>
      <PageHeader title="Order Center" subtitle={branch?.name} icon={ClipboardList} />

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {[
          ["Order Hari Ini", summary.orders_today, ""],
          ["Antre Rangkai", summary.to_arrange, "text-sky-600"],
          ["Dirangkai", summary.arranging, "text-amber-600"],
          ["Siap QC", summary.ready_for_qc, "text-emerald-600"],
          ["Terlambat", summary.overdue, "text-rose-600"],
          ["Belum Lunas", summary.unpaid, "text-rose-600"],
        ].map(([label, val, tone]) => (
          <Card key={label} className="p-3.5">
            <div className={`text-xl font-bold ${tone || "text-slate-800"}`}>{val ?? 0}</div>
            <div className="text-xs text-slate-400 mt-0.5">{label}</div>
          </Card>
        ))}
      </div>

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari no order / nama / telepon..."
              data-testid="order-search"
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-300 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="range-filter" className="h-10 px-3 rounded-xl border border-slate-300 text-sm">
            {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <div className="flex rounded-xl border border-slate-300 overflow-hidden">
            {[["", "Semua"], ["pickup", "Diambil"], ["delivery", "Dikirim"]].map(([v, l]) => (
              <button key={v} onClick={() => setFulfillment(v)} className={`px-3 h-10 text-sm font-medium ${fulfillment === v ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {FILTERS.map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              data-testid={`filter-${v || "all"}`}
              className={`px-3 h-8 rounded-full text-xs font-medium transition ${filter === v ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </Card>

      {loading ? (
        <Loading />
      ) : !data?.orders?.length ? (
        <EmptyState icon={ClipboardList} title="Tidak ada order" message="Belum ada order untuk filter ini." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 font-medium">No Order</th>
                  <th className="py-3 px-4 font-medium">Pelanggan</th>
                  <th className="py-3 px-4 font-medium">Jenis</th>
                  <th className="py-3 px-4 font-medium">Ambil/Kirim</th>
                  <th className="py-3 px-4 font-medium">Status</th>
                  <th className="py-3 px-4 font-medium">Bayar</th>
                  <th className="py-3 px-4 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => setSelected(o.id)}
                    className="border-b border-slate-50 hover:bg-primary/5 cursor-pointer"
                    data-testid={`order-row-${o.order_no}`}
                  >
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-800">{o.order_no}</div>
                      {o.priority !== "normal" && <Badge className={`${PRIORITY_BADGE[o.priority]} mt-1`}>{PRIORITY_LABEL[o.priority]}</Badge>}
                    </td>
                    <td className="py-3 px-4 text-slate-600">{o.customer_name || "-"}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1 text-slate-500 text-xs">
                        {o.fulfillment === "delivery" ? <Truck size={13} /> : <PackageIcon size={13} />}
                        {o.fulfillment === "delivery" ? "Dikirim" : "Diambil"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={o.is_overdue ? "text-rose-600 font-medium" : "text-slate-600"}>{o.due_date ? relDay(o.due_date) : "-"}</span>
                    </td>
                    <td className="py-3 px-4"><Badge className={STATUS_BADGE[o.status]}>{o.status_label || STATUS_LABEL[o.status]}</Badge></td>
                    <td className="py-3 px-4"><Badge className={PAYMENT_BADGE[o.payment_status]}>{PAYMENT_LABEL[o.payment_status]}</Badge></td>
                    <td className="py-3 px-4 text-right font-semibold text-slate-800">{rupiah(o.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {selected && (
        <OrderDetailModal
          orderId={selected}
          transitions={data?.transitions || {}}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </>
  );
}
